import { useEffect, useMemo, useRef, useState } from 'react';
import './multiSelect.css';

/**
 * Sabit rapor kriterleri için ÇOKLU SEÇİM kutusu.
 *
 * NEDEN: kriterler tek seçimli `<select>`ti — kullanıcı "Luppo + İkramlık Çikolata"yı aynı
 * raporda göremiyordu. Bu bileşen aynı yerde birden çok değer seçtirir; seçimler backend'e
 * virgüllü liste olarak gider (ör. groupIds=<guid>,<guid>).
 *
 * Sözleşme:
 *   options  — [{ value, label }]
 *   value    — seçili değerler (string[]); BOŞ = "Tümü" (filtre uygulanmaz)
 *   onChange — (string[]) => void
 *
 * Uzun listelerde (ürün: 1600+) arama kutusu vardır; seçilenler üstte çip olarak durur.
 */
export function MultiSelect({ options = [], value = [], onChange, placeholder = 'Tümü', maxChips = 3 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef(null);

  // Dışarı tıklayınca kapan.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const byValue = useMemo(
    () => Object.fromEntries(options.map((o) => [String(o.value), o.label])),
    [options],
  );
  const filtered = useMemo(() => {
    const term = q.trim().toLocaleLowerCase('tr');
    if (!term) return options;
    return options.filter((o) => String(o.label ?? '').toLocaleLowerCase('tr').includes(term));
  }, [options, q]);

  const toggle = (v) => {
    const s = String(v);
    onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s]);
  };

  const summary = value.length === 0
    ? placeholder
    : value.length <= maxChips
      ? value.map((v) => byValue[v] ?? v).join(', ')
      : `${value.length} seçili`;

  return (
    <div className="rms" ref={boxRef}>
      <button
        type="button"
        className={`rms-control${value.length ? ' has-value' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title={value.length ? value.map((v) => byValue[v] ?? v).join(', ') : placeholder}
      >
        <span className="rms-summary">{summary}</span>
        {value.length > 0 && (
          <span
            className="rms-clear"
            role="button"
            tabIndex={-1}
            title="Seçimi temizle"
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
          >
            ×
          </span>
        )}
        <span className="rms-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="rms-pop">
          {options.length > 8 && (
            <input
              className="rms-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ara…"
              autoFocus
            />
          )}
          <div className="rms-actions">
            <button type="button" className="rms-link" onClick={() => onChange(filtered.map((o) => String(o.value)))}>
              Tümünü seç{q ? ' (aramadaki)' : ''}
            </button>
            <span className="rms-dot">·</span>
            <button type="button" className="rms-link" onClick={() => onChange([])}>Temizle</button>
          </div>
          <div className="rms-list">
            {filtered.length === 0 && <div className="rms-empty">Sonuç yok</div>}
            {filtered.map((o) => {
              const v = String(o.value);
              return (
                <label key={v} className="rms-opt">
                  <input type="checkbox" checked={value.includes(v)} onChange={() => toggle(v)} />
                  <span>{o.label}</span>
                </label>
              );
            })}
          </div>
          <div className="rms-foot">{value.length === 0 ? 'Hiçbiri seçili değil = Tümü' : `${value.length} seçili`}</div>
        </div>
      )}
    </div>
  );
}
